import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import { differenceInBusinessDays, isFuture, isToday } from "date-fns";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import styles from "./PowerOfAttorney.module.css";
import { StepProps } from "../../types";
import { useWebTranslation } from "translations";
import useBreakPoints from "hooks/useMediaQuery";
import { useCurrentInsuranceByAnimalId } from "components/Signup/store/selectors/insurance";
import { useCurrentAnimal } from "components/Signup/store/selectors/animals";
import {
  getCompetitorInsuranceNumber,
  getCompetitorName,
  getCompetitorRenewalDate,
} from "components/Signup/components/utils/insurely/insurelyUtil";
import { dateValid, ssnFormatter } from "utils/validation";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import Colors from "ui-kit/colors";
import Icon from "ui-kit/Icon";
import { Panel } from "ui-kit/Panels";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { useCustomer } from "components/Signup/store/selectors/customer";
import { updateInsuranceById } from "components/Signup/store/slices/animals";
import { useIsLoading } from "components/Signup/store/selectors/metadata";
import {
  UseIsSignaturePresent,
  UseIsSignaturePresentVariables,
} from "../../../../../../__generated__/UseIsSignaturePresent";
import { gql } from "@apollo/client";
import client from "../../../../../../apollo-client";

const useValidateSignature = () => {
  const [status, setStatus] = useState<"pending" | "valid" | "invalid" | null>(
    null
  );

  async function validate(signature: string) {
    setStatus("pending");

    const { data } = await client.query<
      UseIsSignaturePresent,
      UseIsSignaturePresentVariables
    >({
      query: IS_SIGNATURE_PRESENT,
      variables: { input: { binary: signature } },
    });

    setStatus(data.isSignaturePresent ? "valid" : "invalid");

    return Boolean(data.isSignaturePresent);
  }

  return { status, validate };
};

const PowerOfAttorney = ({
  isValid,
  previous,
  next,
  transitioning,
}: StepProps) => {
  const { t } = useWebTranslation();
  const canvasRef = useRef<SignatureCanvas>(null);
  const { isMobile } = useBreakPoints();

  const animal = useCurrentAnimal();
  const isLoading = useIsLoading();
  const dispatch = useSignupDispatch();
  const insurance = useCurrentInsuranceByAnimalId(animal.id);
  const customer = useCustomer();

  const competitorName = getCompetitorName(insurance.insurely);
  const renewalDate = getCompetitorRenewalDate(insurance.insurely);
  const insuranceNumber = getCompetitorInsuranceNumber(insurance.insurely);

  const clientName = insurance.insurely?.clientName;
  const customerNameAndSsn = `${clientName ?? ""}${
    clientName ? ", " : ""
  } ${ssnFormatter(customer.ssn ?? "")}`;

  const [showPlaceholder, setShowPlaceholder] = useState<boolean>(
    !insurance.insurely?.customerSignature
  );

  const { status, validate } = useValidateSignature();

  useEffect(() => {
    canvasRef.current?.clear();

    if (insurance.insurely?.customerSignature) {
      dispatch(
        updateInsuranceById({
          id: insurance.id,
          update: {
            insurely: {
              ...insurance.insurely,
              customerSignature: null,
            },
          },
        })
      );
    }
    // eslint-disable-next-line -- Clear out signature on mount until we figure out how to scale it
  }, []);

  const onBegin = () => {
    setShowPlaceholder(false);
  };

  const onClear = () => {
    canvasRef.current?.clear();
    setShowPlaceholder(true);

    dispatch(
      updateInsuranceById({
        id: insurance.id,
        update: {
          insurely: {
            ...insurance.insurely,
            customerSignature: null,
          },
        },
      })
    );
  };

  const onEnd = async () => {
    const signature = canvasRef.current?.toDataURL();

    if (signature) {
      const isValid = await validate(signature);

      if (isValid) {
        dispatch(
          updateInsuranceById({
            id: insurance.id,
            update: {
              insurely: {
                ...insurance.insurely,
                customerSignature: signature,
              },
            },
          })
        );
      }
    }
  };

  const tooShortTimeUntilRenewal =
    renewalDate &&
    differenceInBusinessDays(new Date(renewalDate), new Date()) <=
      parseInt(
        t(
          "choose_start_date.cancel_competitor.too_short_time_until_renewal.min_days"
        ),
        10
      );

  const cancellationPossible =
    !!competitorName &&
    renewalDate &&
    dateValid(renewalDate) &&
    !tooShortTimeUntilRenewal &&
    insurance.startDate &&
    (isToday(new Date(insurance.startDate)) ||
      isFuture(new Date(insurance.startDate)));

  return (
    <>
      <ChatContainer leaving={transitioning}>
        <>
          <ChatMessage>
            <Typography>
              {t("cancel_competitor.power_of_attorney.msg_1", {
                competitorName: competitorName ?? "",
              })}
            </Typography>
          </ChatMessage>
          {cancellationPossible && (
            <div className={styles.signatureChatMessage}>
              <Typography variant="body2">
                <strong>
                  {t("choose_start_date.cancel_competitor.title")}
                </strong>
              </Typography>
              <Gutter size={1} />
              <Typography variant="body3">
                {t("choose_start_date.cancel_competitor.note", {
                  competitorName: competitorName ?? "",
                  insuranceNumber: insuranceNumber ?? "",
                  renewalDate: renewalDate ?? "",
                })}
              </Typography>
              <Gutter size={2} />

              <div
                className={styles.signatureWrapper}
                style={{ borderColor: Colors.green200 }}
                onClick={() => {
                  setShowPlaceholder(false);
                }}
              >
                {/* @ts-ignore */}
                <SignatureCanvas
                  backgroundColor="transparent"
                  penColor={Colors.blue300}
                  ref={canvasRef}
                  canvasProps={{
                    width: isMobile ? 300 : 500,
                    height: 120,
                    className: "sigCanvas",
                  }}
                  onBegin={onBegin}
                  onEnd={onEnd}
                />
                {showPlaceholder && <div className={styles.signPlaceholder} />}
                {(!canvasRef.current?.isEmpty() || status !== "pending") && (
                  <div className={styles.clear} onClick={onClear}>
                    <Icon name="quiz_wrong" size={24} color={Colors.error} />
                  </div>
                )}
                <div className={styles.signatureLineAndName}>
                  <div className={styles.signatureLine} />
                  <Typography variant="body3">{customerNameAndSsn}</Typography>
                </div>
              </div>
              <div style={{ marginLeft: 8 }}>
                <Typography variant="body3">
                  {`${t("choose_start_date.date")}: `}
                  {new Date().toLocaleDateString("sv")}
                </Typography>
              </div>
            </div>
          )}

          {status === "invalid" && (
            <>
              <Gutter size={2} />
              <ErrorMessage>
                <Typography color={"error"}>
                  {t(
                    "cancel_competitor.power_of_attorney.error.invalid_signature"
                  )}
                </Typography>
              </ErrorMessage>
              <Gutter size={2} />
            </>
          )}

          {tooShortTimeUntilRenewal && (
            <>
              <Gutter size={2} />
              <Panel
                variant="disclaimer"
                title={t(
                  "choose_start_date.cancel_competitor.too_short_time_until_renewal.title"
                )}
              >
                <Typography variant="body1">
                  {t(
                    "choose_start_date.cancel_competitor.too_short_time_until_renewal.msg_1",
                    {
                      competitorName: competitorName ?? "",
                    }
                  )}
                </Typography>
                <Gutter size={1} />
                <Typography variant="body1">
                  {t(
                    "choose_start_date.cancel_competitor.too_short_time_until_renewal.msg_2",
                    {
                      competitorName: competitorName ?? "",
                      renewalDate: renewalDate ?? "",
                    }
                  )}
                </Typography>
              </Panel>
            </>
          )}
        </>
        <FlowButtons
          inProgress={transitioning || isLoading || status === "pending"}
          backButton={false}
          next={next}
          nextTitle={t("cancel_competitor.power_of_attorney.btn.continue")}
          disabled={
            !isValid ||
            status === "pending" ||
            status === "invalid" ||
            !cancellationPossible
          }
          previous={previous}
        />
      </ChatContainer>
    </>
  );
};

export default PowerOfAttorney;

const IS_SIGNATURE_PRESENT = gql`
  query UseIsSignaturePresent($input: IsSignaturePresentInput) {
    isSignaturePresent(input: $input)
  }
`;
